import React, { useEffect, useState } from 'react'
import { Select, Table } from 'antd'
import { useTranslation } from 'react-i18next'
import styles from './results-section.module.scss'
import AlertComponent from '@BUP/components/Generics/Alert'
import BupSpinner from '@BUP/components/Loading/Spinner'
import ButtonComponent from '@BUP/components/Generics/Buttons/button-component'
import SubtitleDashboard from '@BUP/components/Generics/SubtitleDashboard'
import UserIcon from '@BUP/assets/Icons/user_icon.svg'
import { useGetResultVersions } from '@BUP/hooks/CourseAssignment/useGetResultVersions'
import { useGetResultVersion } from '@BUP/hooks/CourseAssignment/useGetResultVersion'
import { useSaveResultEdits } from '@BUP/hooks/CourseAssignment/useSaveResultEdits'
import { useDownloadResultXlsx } from '@BUP/hooks/CourseAssignment/useDownloadResultXlsx'
import { computeCourseMetrics } from '@BUP/utils/courseAssignmentResults'
import CourseColumn from './CourseColumn'

const { Option } = Select

const countGender = (students, gender) => students.filter(student => student.gender === gender).length

/**
 * SDD Paso 6-8 for one period (= one grade): pick a result version and browse it - a summary table
 * of every resulting course, then a board with every course side by side as a column of student
 * cards (current course, gender, classmates achieved), so students can be dragged from one course
 * to another.
 *
 * The school doesn't upload results: BRAVE UP!'s team runs course-generator and loads its files
 * from the admin (DATA! > Asignación de cursos), so here they only show up as versions.
 *
 * A drag only updates a local "working copy" (counts/% live-recomputed via computeCourseMetrics,
 * same formula the backend uses) until "Guardar cambios" POSTs the accumulated moves - which
 * creates a brand new version server-side rather than mutating the one being viewed (see
 * createEditedVersion).
 *
 * "Descargar" downloads the version on screen as an `.xlsx`, so the file always matches a saved
 * version - it's disabled while there are unsaved moves, which wouldn't be in it.
 * `fileSuffix` ("-<escuela>-<grado>-<año>", see buildCourseGeneratorFileSuffix) keeps downloads of
 * different schools/grades apart.
 */
const ResultsSection = ({ periodId, fileSuffix = '' }) => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPeriodDashboard' })
  const [downloadError, setDownloadError] = useState(null)
  const [selectedVersionId, setSelectedVersionId] = useState(null)
  const [workingCourses, setWorkingCourses] = useState(null)
  const [pendingMoves, setPendingMoves] = useState(new Map()) // resultStudentId -> targetCourseName

  const { versions, isLoading: isLoadingVersions, refetch: refetchVersions } = useGetResultVersions(periodId)
  const { version, isLoading: isLoadingVersion, error: errorVersion } = useGetResultVersion(periodId, selectedVersionId)
  const { mutateAsync: saveResultEdits, isPending: isSaving } = useSaveResultEdits()
  const { mutateAsync: downloadResultXlsx, isPending: isDownloading } = useDownloadResultXlsx()

  useEffect(() => {
    if (!selectedVersionId && versions.length) {
      setSelectedVersionId(versions[0].id)
    }
  }, [versions, selectedVersionId])

  // Reset the editable working copy whenever a different version loads (including right after
  // saving edits, once the new version comes back from the server).
  useEffect(() => {
    setWorkingCourses(version?.courses ?? null)
    setPendingMoves(new Map())
  }, [version])

  const handleDownload = async () => {
    setDownloadError(null)
    try {
      await downloadResultXlsx({
        periodId,
        versionId: selectedVersionId,
        fileName: `${t('resultDownloadFileName')}${fileSuffix}-v${version.versionNumber}.xlsx`
      })
    } catch {
      setDownloadError(t('resultDownloadError'))
    }
  }

  const handleMoveStudent = (resultStudentId, targetCourseName) => {
    const sourceCourse = workingCourses.find(course => course.students.some(s => String(s.id) === resultStudentId))
    if (!sourceCourse || sourceCourse.name === targetCourseName) return

    setWorkingCourses(currentCourses => {
      const movedStudent = sourceCourse.students.find(s => String(s.id) === resultStudentId)
      return currentCourses.map(course => {
        if (course.name === sourceCourse.name) {
          const students = course.students.filter(s => String(s.id) !== resultStudentId)
          return { ...course, students, ...computeCourseMetrics(students) }
        }
        if (course.name === targetCourseName) {
          const students = [...course.students, movedStudent]
          return { ...course, students, ...computeCourseMetrics(students) }
        }
        return course
      })
    })

    // Dragging a student back to the course they started in (in the saved version) undoes the
    // move instead of recording a no-op one - otherwise saving would create a version identical
    // to the current one.
    const originalCourse = version?.courses.find(course => course.students.some(s => String(s.id) === resultStudentId))
    setPendingMoves(current => {
      const next = new Map(current)
      if (originalCourse?.name === targetCourseName) {
        next.delete(resultStudentId)
      } else {
        next.set(resultStudentId, targetCourseName)
      }
      return next
    })
  }

  const handleDiscardChanges = () => {
    setWorkingCourses(version?.courses ?? null)
    setPendingMoves(new Map())
  }

  const handleSaveChanges = async () => {
    const moves = [...pendingMoves.entries()].map(([resultStudentId, targetCourseName]) => ({
      resultStudentId,
      targetCourseName
    }))
    const newVersion = await saveResultEdits({ periodId, versionId: selectedVersionId, moves })
    setSelectedVersionId(newVersion.id)
    refetchVersions()
  }

  const hasPendingMoves = pendingMoves.size > 0
  const movedStudentIds = new Set(pendingMoves.keys())
  const hasCriteriaBalance = (workingCourses || []).some(course => Object.keys(course.criteriaBalance || {}).length)

  const formatCriteriaBalance = (criteriaBalance) => Object.entries(criteriaBalance || {})
    .map(([dimension, counts]) => {
      const countsText = Object.entries(counts).map(([value, count]) => `${value} (${count})`).join(', ')
      return `${dimension}: ${countsText}`
    })
    .join(' · ')

  const summaryColumns = [
    { title: t('resultColumnCourse'), dataIndex: 'name', key: 'name' },
    { title: t('resultColumnStudents'), dataIndex: 'studentCount', key: 'studentCount' },
    { title: t('resultColumnMale'), key: 'male', render: (_, course) => countGender(course.students, 'H') },
    { title: t('resultColumnFemale'), key: 'female', render: (_, course) => countGender(course.students, 'M') },
    {
      title: t('resultColumnPreferencesAchieved'),
      key: 'preferencesAchievedPercent',
      render: (_, course) => course.preferencesAchievedPercent === null ? '—' : `${course.preferencesAchievedPercent}%`
    },
    ...(hasCriteriaBalance
      ? [{
          title: t('resultCriteriaBalance'),
          key: 'criteriaBalance',
          render: (_, course) => formatCriteriaBalance(course.criteriaBalance)
        }]
      : [])
  ]

  const renderVersion = () => {
    if (isLoadingVersion) return <BupSpinner size='small' />
    if (errorVersion) {
      return (
        <AlertComponent
          type='error'
          messageKey='common.resources.course-assignment.actions.read.failure'
          error={errorVersion}
        />
      )
    }
    if (!version || !workingCourses) return null

    return (
      <>
        {version.unmatchedStudentNames?.length > 0 && (
          <AlertComponent
            type='warning'
            message={t('resultUnmatchedNames', { names: version.unmatchedStudentNames.join(', ') })}
          />
        )}

        <Table
          className={styles.summary_table}
          columns={summaryColumns}
          dataSource={workingCourses}
          rowKey='id'
          pagination={false}
          size='small'
        />

        <SubtitleDashboard icon={UserIcon} text={t('resultCoursesDetailTitle')} />
        <p>{t('resultDragHint')}</p>
        {hasPendingMoves && (
          <div className={styles.controls_wrapper}>
            <span>{t('resultPendingMoves', { count: pendingMoves.size })}</span>
            <ButtonComponent
              buttonText={t('resultSaveChangesButton')}
              colorType='primary'
              onClick={handleSaveChanges}
              disabled={isSaving}
            />
            <ButtonComponent
              buttonText={t('resultDiscardChangesButton')}
              colorType='tertiary'
              onClick={handleDiscardChanges}
              disabled={isSaving}
            />
          </div>
        )}
        <div className={styles.board}>
          {workingCourses.map(course => (
            <CourseColumn
              key={course.id}
              course={course}
              movedStudentIds={movedStudentIds}
              onDropStudent={handleMoveStudent}
              isEditable={!isSaving}
            />
          ))}
        </div>
      </>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.upload_wrapper}>
        <p>{t('resultSourceDescription')}</p>
        <div className={styles.controls_wrapper}>
          {versions.length > 0 && (
            <Select
              className={styles.version_select}
              value={selectedVersionId}
              onChange={setSelectedVersionId}
              disabled={hasPendingMoves}
              aria-label={t('resultVersionSelectLabel')}
            >
              {versions.map(v => (
                <Option key={v.id} value={v.id}>
                  {t('resultVersionLabel', { number: v.versionNumber })}
                </Option>
              ))}
            </Select>
          )}
          {version && (
            <ButtonComponent
              buttonText={t('resultDownloadButton')}
              colorType='tertiary'
              size='small'
              onClick={handleDownload}
              disabled={isDownloading || hasPendingMoves}
            />
          )}
          {version && hasPendingMoves && <span>{t('resultDownloadPendingHint')}</span>}
        </div>
        {downloadError && <p className={styles.error_text}>{downloadError}</p>}
      </div>

      {isLoadingVersions
        ? <BupSpinner size='small' />
        : versions.length === 0
          ? <p>{t('resultNoVersionsYet')}</p>
          : renderVersion()}
    </div>
  )
}

export default ResultsSection
